import size from 'lodash/size';
import get from 'lodash/get';
import find from 'lodash/find';
import filter from 'lodash/filter';
import reduce from 'lodash/reduce';
import isArray from 'lodash/isArray';
import getConfig from '~/config';
import { getOrFail } from 'mythil/object';
import identify from "~/identify";
import {findOrFail} from "mythil/array";
import modelTree from '~/selectors/modelTree';
import {isModelViewValid, presentAllResultParams, presentSearchResultParams} from "~/statePresenter";
import {stateTrans} from "~/translations";
import hashCode from 'mythil/hashCode'
import { getTranslator } from '~/translations';
import pickBy from 'lodash/pickBy';
let emptyObject;

const deduceStateEntry = (state, modelId, key) => {
    let modelConfigKeys = ['dataModel', 'stateModel'];
    
    if(modelConfigKeys.indexOf(key.split('.')[0]) > -1) {
        return getDataModelProp(state, modelId, key);
    }
    
    return getModelPropOrFail(state, modelId, key);
};

export function getModelOrNull(state, name) {
    let model = get(state, `models.${name}`);

    if(model && model.finished_at) {
        return model.value;
    }

    return null;
}

export function getModel(state, id) {
    return getOrFail(state.ui.loadedModels, id);
}

export function getModels(state) {
    return reduce(state.models, (models, modelState, modelName) => {
        if(modelState.value && modelState.finished_at) {
            models[modelName] = modelState.value;
        }

        return models;
    }, {});
}

export function getHelpState(state) {
    return state.help.helpData;
}

export function getHelpTerm(state) {
    return state.help.term;
}

export function getHelpCategory(state) {
    return state.help.category;
}

export function getMediaCategory(state) {
    return state.help.mediaCategory;
}

export function getMediaIndex(state) {
    return state.help.mediaIndex;
}

export function getMediaTime(state) {
    return state.help.mediaTime;
}

export function getHelpEntries(state) {
    let helpState = getHelpState(state);
    return get(helpState, 'value.results.entries');
}

export function getHelpEntry(state, name) {
    let helpState = getHelpState(state);
    return get(helpState, `value.results.indexed.${name}`);
}

export function getIndexedHelpEntries(state) {
    let helpState = getHelpState(state);
    return get(helpState, `value.results.indexed`);
}

export function getHelpMedia(state, name) {
    let entry = getHelpEntry(state, name);
    return entry.media;
}

export function areHelpResultsLoaded(state) {
    let helpState = getHelpState(state);
    return !(!helpState.finished_at || helpState.invalidated_at > helpState.finished_at);
}

export function isHelpModelDataLoaded(state) {
    return isDataModelLoaded(state, 'help_articles');
}

export function isHelpLoaded(state) {
    let dataLoaded = isHelpModelDataLoaded(state);

    return areHelpResultsLoaded(state) && dataLoaded;
}

export function getModelPropOrFail(state, modelId, path, error) {
    if(!modelId) {
        throw new Error('getModelProp failed: modelId is not specified.')
    }

    return getOrFail(state, `ui.loadedModels.${modelId}.${path}`);
}

export function getModelPropOrDefault(state, modelId, path, defaultValue = null) {
    return get(state, `ui.loadedModels.${modelId}.${path}`, defaultValue);
}

export function getDataModelProp(state, modelId, path, error) {
    return getOrFail(getDataModelByModelId(state, modelId), path, error);
}

export function getDataModelPropOrDefault(state, modelId, path, defaultVal = null) {
    return get(getDataModelByModelId(state, modelId), path, defaultVal);
}

export function getModelName(state, modelId) {
    if(isAvailableModel(state, modelId)) {
        return modelId;
    }

    return getModelPropOrFail(state, modelId, `modelName`);
}

export function getModelNameOrNull(state, modelId) {
    if(isAvailableModel(state, modelId)) {
        return modelId;
    }

    return getModelPropOrDefault(state, modelId, `modelName`);
}

export function getLoadedModels(state) {
    return state.ui.loadedModels;
}

export function getDataModelState(state, modelId) {
    let dataModelName = getModelName(state, modelId);
    return getOrFail(state, `models.${dataModelName}`);
}

export function isDataModelLoaded(state, modelId) {
    let dataModelName = getModelNameOrNull(state, modelId);
    if(!dataModelName) { return false; }

    let dataModelState = get(state, `models.${dataModelName}`);

    if (!dataModelState) {
        return false;
    }

    return !!dataModelState.finished_at;
}

export function isDataModelLoading(state, modelId) {
    let dataModelName = getModelNameOrNull(state, modelId);
    let dataModelState = get(state, `models.${dataModelName}`);

    if(!dataModelState) { return false;}

    return dataModelState.started_at && !dataModelState.finished_at;
}

export function isDataModelValid(state, modelId) {
    try {

        let dataModelName = getModelName(state, modelId),
            dataModelState = get(state, `models.${dataModelName}`);

        if(!dataModelState) { return false;}

        if(!dataModelState.finished_at) {
            return false;
        }

        return dataModelState.status !== identify.STATUS_INVALIDATED;
    } catch(e) {
        throw e;
    }
}


export function getDirectColumnVisibility(state, identifier) {
    try {
        return getTableSetting(state, identifier, "columnVisibility");
    } catch (e) {
        return getRawTableSetting(state, identifier, `columnVisibility`, emptyObject);
    }
}

export function getcolumnVisibility(state, modelId) {
    return getDirectColumnVisibility(state, modelId);
}

const emptyColumnOrderObject = {}
export function getDirectColumnOrder(state, identifier) {
    try {
        return getTableSetting(state, identifier, "columnOrder");
    } catch (e) {
        return getRawTableSetting(state, identifier, `columnOrder`, emptyColumnOrderObject);
    }
}

export function getColumnOrder(state, modelId) {
    return getDirectColumnOrder(state, modelId);
}

export function getDataModelByModelId(state, id) {
    if(isAvailableModel(state, id)) {
        return state.models[id].value;
    }

    return getDataModelState(state, id).value;
}

export function getDataModel(state, modelName) {
    return getOrFail(state, `models.${modelName}.value`, `Failed to get dataModel with name ${modelName}`)
}

export function getLanguage(state) {
    return state.ui.language;
}

export function getFallbackLanguage(state) {
    let fallbackLocale = state.ui.fallback_language;
    return fallbackLocale;
}

export function getFilters(state, modelId) {
    return getDataModelProp(state, modelId, `dataModel.resultParams.filters`);
}

export function getStartValues(state, modelId) {
    return getTyped(state, modelId, identify.EDITFIELD, 'startValues');
}

const emptySelectedRowsObject = {}
export function getSelectedRows(state, modelId) {
    const selectThroughout = getSelectThroughout(state, modelId);
    const selections = getModelPropOrDefault(state, modelId, "selectedItems", emptySelectedRowsObject);
    return Object.keys(pickBy(selections, (selected) => selected === !selectThroughout))
}

export function getSelectThroughout(state, modelId) {
    return getModelPropOrDefault(state, modelId, "selectThroughout", false)
}

// Todo: duplicate of stateTrans?
export function getTranslation(state, key, data) {
    let lang  = getLanguage(state),
        fallbackLang = getFallbackLanguage(state);

    let translator = getTranslator(lang,fallbackLang);

    return translator(key, data);
}

export function getModelTooltip(state, modelId, tooltipName) {
    return getDataModelPropOrDefault(state, modelId, `dataModel.tooltips.${tooltipName}`);
}

export function getModelTooltipDirect(state, modelName, tooltipName) {
    let dataModel = getDataModel(state, modelName);
    return get(dataModel, `dataModel.tooltips.${tooltipName}`);
}

export function getAllResultParams(state, modelId) {
    const { isCalendarEnabled, calendarView } = getCalendar(state, modelId);
    return {
        isCalendarEnabled,
        calendarView,
        view: getView(state, modelId),
        ...getSubviewItemData(state, modelId),
        itemId: getItemId(state, modelId),
        modelName: getModelName(state, modelId),
        groupStates: getGroupStates(state, modelId),
        filters: getFilters(state, modelId),
        filterValues: getFilterValues(state, modelId),
        initialFilterValues: getInitialFilterValues(state, modelId),
        page: getResultsPage(state, modelId),
        sortOptions: getResultSortOptions(state, modelId),
        perPage: getResultPerPage(state, modelId),
        columns: getColumns(state, modelId),
        columnOrder: getColumnOrder(state, modelId),
        columnVisibility: getcolumnVisibility(state, modelId),
    }
}

export function getFormattedResultParams(state, modelId) {
    let props = getAllResultParams(state, modelId);
    return presentAllResultParams(props);
}

export function getPredefinedViews(state, modelId) {
    return getDataModelProp(state, modelId, `stateModel.resultParams.predefinedViews`);
}

export function getInitialFilterValues(state, modelId) {
    return getDataModelProp(state, modelId, `stateModel.resultParams.filters`);
}

export function getEmptyFilterValues(state, modelId) {
    return getDataModelProp(state, modelId, `dataModel.emptyFilterValues`);
}

export function getInitialValuesByFilterName(state, modelId, filterName, path) {
    return get(getInitialFilterValues(state, modelId), [filterName, path].filter(Boolean).join("."));
}

export function getTyped(state, modelId, inputType, type, key = null) {
    let baseKey  = getOrFail(getConfig().inputTypes, `${inputType}.${type}`);

    return key
        ? deduceStateEntry(state, modelId, `${baseKey}.${key}`)
        : deduceStateEntry(state, modelId, baseKey);
}

export function getFieldData(state, modelId, inputType, name) {
    return getTyped(state, modelId, inputType, 'data', name);
}

export function getFieldValue(state, modelId, inputType, name) {
    return getTyped(state, modelId, inputType, 'value', name);
}

export function getFilterValue(state, modelId, inputType, name) {
    try {
        const filters = getFilterValues(state, modelId);
        return getOrFail(filters, name)
    } catch {
        return getOrFail(getEmptyFilterValues(state, modelId), name);
    }
}

export function getInitialFieldValue(state, modelId, inputType, name) {
    return getTyped(state, modelId, inputType, 'initialValue', name);
}

export function getFieldQuery(state, modelId, inputType, name) {
    return getTyped(state, modelId, inputType, 'options', `${name}.query`);
}

export function getAutocomplete(state, modelId, inputType, name) {
    return getTyped(state, modelId, inputType, 'options', `${name}.autocomplete`);
}

export function getAutocompleteOptions(state, modelId, inputType) {
    return getTyped(state, modelId, inputType, 'options');
}

export function getFieldValues(state, modelId, inputType){
    return getTyped(state, modelId, inputType, 'value');
}

export function getItemData(state, modelId) {
    return getDataModelProp(state, modelId, `dataModel.itemData`);
}

export function getFilterOptions(state, modelId) {
    return getTyped(state, modelId, identify.FILTERFIELD, 'options');
}

export function getEditFieldOptions(state, modelId) {
    return getTyped(state, modelId, identify.EDITFIELD, 'options');
}

export function getValueAdaptData(state, modelId) {
    let data = getSubviewItemData(state, modelId);

    data.itemId = getItemId(state, modelId);
    data.editFields = getEditFields(state, modelId);
    data.filters = getFilters(state, modelId);

    return data;
}

export function getValueAdaptSources(state, modelId) {
    let sources = {};

    sources.initialEditFieldValues = getEditFieldInitialValues(state, modelId);
    sources.editFieldValues = getEditFieldValues(state, modelId);
    sources.emptyFilterValues = getEmptyFilterValues(state, modelId);
    sources.filterValues = getFilterValues(state, modelId);
    sources.initialFilterValues = getInitialFilterValues(state, modelId);
    sources.filterOptions = getFilterOptions(state, modelId);
    sources.editFieldOptions = getEditFieldOptions(state, modelId);

    return sources;
}


/**
 * returns an object with all the data needed to render a field. Note: needs to be expanded into the mapStateToProps function so that redux can compare the object reference
 *
 * @param state
 * @param modelId
 * @param inputType
 * @param name
 * @returns {{field: *, value: *, initialValue: *, validationMessage?: string, query: string, autocomplete?: any,}}
 */
export function getField(state, modelId, inputType, name) {
    const getTypedValue = {
        [identify.EDITFIELD]: () => getFieldValue(state, modelId, inputType, name),
        [identify.FILTERFIELD]: () => getFilterValue(state, modelId, inputType, name),
    }
    let props = {
        value: getTypedValue[inputType](),
        field: getFieldData(state, modelId, inputType,  name),
        initialValue: getInitialFieldValue(state, modelId, inputType, name),
    };

    if(props.field.isAutocomplete) {
        props.autocomplete = getAutocomplete(state, modelId, inputType, name)
    }

    if(props.field.options) {
        props.query = getFieldQuery(state, modelId, inputType, name);
    }

    if(inputType === identify.EDITFIELD) {
        props.validationMessage = getEditFieldValidationMessage(state, modelId, name);
    }

    return props;
}



export function getSingleTitle(state, modelId) {
    return getDataModelProp(state, modelId, 'dataModel.singleTitle');
}

export function getFieldOptionsArr(state, modelId, inputType, name) {
    let fieldData = getFieldData(state, modelId, inputType,  name);

    if(!fieldData.isAutocomplete) {
        return fieldData.options;
    }

    return getAutocomplete(state, modelId, inputType, name).value;
}

export function getFieldOptionValue(state, modelId, inputType, fieldName, optionValue, optionName) {
    let options = getFieldOptionsArr(state, modelId, inputType, fieldName);
    return findOrFail(options, { value: optionValue })[optionName];
}

export function getFieldGrid(state, modelId, gridName = 'default') {
    return getDataModelProp(state, modelId, `dataModel.item.fieldGrid.${gridName}`);
}

export function getFieldGridVisiblitySources(state, modelId, gridName = 'default') {
    return getDataModelPropOrDefault(state, modelId, `dataModel.item.fieldGridVisibilitySources.${gridName}`, []);
}

export function getFieldGridControlSources(state, modelId, gridName = 'default') {
    return getDataModelPropOrDefault(state, modelId, `dataModel.item.fieldGridControlSources.${gridName}`, {});
}

export function getSetCurrentModelId(state) {
    return state.ui.currentModelId;
}

export function getRootModelId(state) {
    let modelId = getCurrentModelId(state);
    while (true) {
        const parentModelId = getCurrentModelId(state, modelId);
        if (parentModelId === modelId) return modelId;
        modelId = parentModelId;
    }
}

export function getCurrentModelId(state, modelId) {
    let setCurrentModelId = modelId ?? getSetCurrentModelId(state);
    if(!setCurrentModelId) { return setCurrentModelId; }

    try {
        let itemId = getItemId(state, setCurrentModelId);

        if(!itemId) { return setCurrentModelId; }

        let previousView = getPreviousView(state, setCurrentModelId);

        if(previousView === identify.VIEW_TABULAR) { return setCurrentModelId; }
        
        const subviewData = getParentSubview(state, setCurrentModelId);
        if (subviewData.parentSubview?.relationField) {
            return subviewData.parentModelId;
        }
        
        let parentModelId = getParentId(state, setCurrentModelId);
        if(parentModelId) {
            return parentModelId;
        }

        return setCurrentModelId;
    } catch(e) {
        return setCurrentModelId;
    }
}

export function getDialogModelId(state) {
    let setCurrentModelId = getSetCurrentModelId(state),
        currentModelId = getCurrentModelId(state);

    if(setCurrentModelId !== currentModelId) {
        return setCurrentModelId;
    }

    return null;
}



export function getSubviewTree(state) {
    let setCurrentModelId = getSetCurrentModelId(state);
    let tree = [];
    try {
        while (true) {
            const currentModelId = getCurrentModelId(state, setCurrentModelId);
            if (currentModelId === setCurrentModelId) return Object.fromEntries(tree);

            const hasSubviews = !!size(filter(getSubviews(state, setCurrentModelId), (subview) => !subview.relationField));
            tree.unshift([setCurrentModelId, hasSubviews]);
            setCurrentModelId = currentModelId;
        }
    } catch(e) {
        return []
    }
}

export function getFieldGridRow(state, modelId, name, gridName = 'default') {
    return getDataModelProp(state, modelId, `dataModel.item.fieldGrid.${gridName}.rows.${name}`);
}

export function getFieldGridRowState(state, modelId, name) {
    return getModelPropOrFail(state, modelId, `item.rows.${name}`)
}
export function getFilterValues(state, modelId) {
    return getModelPropOrDefault(state, modelId, `activeView.filters`, getInitialFilterValues(state, modelId));
}

export function getItemId(state, modelId) {
    return getModelPropOrDefault(state, modelId, `item.id`);
}

export function getFirstItemId(state, modelId) {
    let itemId = getItemId(state, modelId);
    
    if(!itemId || itemId.length < 1) {
        return getLoadedItemId(state, modelId);
    }
    
    return itemId;
}

export function getEditFieldValues(state, modelId) {
    return getFieldValues(state, modelId, identify.EDITFIELD);
}

export function getInitialValues(state, modelId, inputType) {
    return getTyped(state, modelId, inputType, 'initialValue');
}

export function getEditFieldInitialValues(state, modelId) {
    return getInitialValues(state, modelId, identify.EDITFIELD);
}

export function getSubviewCount(state, modelId) {
    return size(getSubviews(state, modelId));
}

export function getItemRow(state, modelId) {
    let itemId = getItemId(state, modelId);

    // console.log('XX getItemRow', itemId, getResultsState(state, modelId));

    return get(getResultsValue(state, modelId), `results.${itemId}`);
}

export function getFieldDatas(state, modelId, inputType) {
    return getTyped(state, modelId, inputType, 'data');
}

// TODO: this should be renamed to getEditFieldDatas
export function getEditFields(state, modelId) {
    return getFieldDatas(state, modelId, identify.EDITFIELD);
}

export function getEditFieldData(state, modelId, fieldName) {
    let modelName = getModelName(state, modelId);

    return /** @type { import('./data/types/field.types').PreparedField} */(getOrFail(state, `models.${modelName}.value.dataModel.itemData.editFields.${fieldName}`));
}

export function getSaveButtonProps(state, modelId) {
    try {
        return getItemDataState(state, modelId).value.saveButton;
    } catch {
        return;
    }
}

export function getRowColumn(state, modelId, itemId, name) {
    return getOrFail(getResultsValue(state, modelId), `results.${itemId}.${name}`);
}

export function getColumns(state, modelId) {
    return getDataModelProp(state, modelId, `dataModel.resultParams.columns`);
}

export function getResultsState(state, modelId) {
    return getModelPropOrFail(state, modelId, `results`, `Failed to get results for ${modelId}.`)
}

export function getView(state, modelId) {
    return getSetView(state, modelId);
}

export function getGridLevel(state, modelId, gridName = 'default') {
    return getDataModelProp(state, modelId, `dataModel.item.fieldGrid.${gridName}.maxLevel`);
}

export function getItemView(state, modelId) {
    let view = getSetView(state, modelId);

    if(!isModelViewValid(view)) {
        return getPreviousView(state, modelId);
    }

    return view;
}

export function getSetView(state, modelId) {
    return getModelPropOrFail(state, modelId, `view`);
}

export function getPreviousView(state, modelId) {
    return getModelPropOrFail(state, modelId, `previousView`);
}

export function getResultsValue(state, modelId) {
    return getModelPropOrFail(state, modelId, `results.value`, `Failed to get results.value for ${modelId}.`)
}

export function getResultsPage(state, modelId) {
    return getModelPropOrFail(state, modelId, `results.value.page`, `Failed to get results.value for ${modelId}.`)
}

export function getResultParams(state, modelId) {
    return getModelPropOrFail(state, modelId, `resultParams`);
}

export function getTooltipRead(state, modelId, context, title) {
    let titleHash = hashCode(title);
    return getTableSetting(state, modelId, `${context}.readTooltips.${titleHash}`, false);
}

export function getTableSetting(state, modelId, setting, defaultValue = null) {
    return get(getModel(state, modelId), `activeView.${setting}`, defaultValue);
}

export function getRawTableSetting(state, identifier, setting, defaultValue = null) {
    let { activeFilteredView, filteredViews, ...modelSettings }  = get(state.ui.settings, `${identifier}`, defaultValue) || {};
    return get(filteredViews, `${activeFilteredView}.${setting}`) ?? get(modelSettings, setting) ?? defaultValue;
}

export function getTooltipsActive(state, ) {
    /* Hardcode disable tooltips */
    return false && !get(state.ui.settings, 'tooltipsHidden', true);
}

export function getResultSortOptions(state, modelId) {
    let sortSettings = getTableSetting(state, modelId, 'sortOptions');

    if(sortSettings) { return sortSettings; }

    return getModelPropOrFail(state, modelId, `resultParams.sortOptions`);
}

const emptyColumnWidthsObject = {};
export function getColumnWidths(state, modelId) {
    return getTableSetting(state, modelId, 'columnWidths', emptyColumnWidthsObject);
}

export function getCalendarViews(state, modelId) {
    return getDataModelProp(state, modelId, `dataModel.calendars`);
}

const emptyCalendarViewConfig = {};
export function getCalendarViewConfig(state, modelId) {
    return getTableSetting(state, modelId, 'calendarViewConfig', emptyCalendarViewConfig);
}

export function getSelectedCalendarView(state, modelId) {
    const [calendarView] = getCalendarViews(state, modelId) || [ null ];
    return calendarView;
}

export function getCalendar(state, modelId) {
    const isParentView = !getParentId(state, modelId);
    const hasCalendarViews = isParentView && getCalendarViews(state, modelId).length > 0;

    return {
        hasCalendarViews,
        calendarViewConfig: getCalendarViewConfig(state, modelId),
        calendarView: getSelectedCalendarView(state, modelId),
        isCalendarEnabled: hasCalendarViews && get(getCalendarViewConfig(state, modelId), 'enabled', false)
    }
}

export function getResultPerPage(state, modelId) {
    const { isCalendarEnabled, calendarView } = getCalendar(state, modelId);
    if (isCalendarEnabled) { return calendarView.max_records; }

    let perPageSetting = getTableSetting(state, modelId, 'perPage');

    if(perPageSetting) { return perPageSetting; }
    
    return getModelPropOrFail(state, modelId, `resultParams.perPage`);
}

export function getGroups(state, modelId) {
    return getDataModelProp(state, modelId, `dataModel.filterGroups`, `Failed to groups for ${modelId}.`);
}

export function getGroup(state, modelId, name) {
    return get(getGroups(state, modelId), name, emptyObject);
}

export function getGroupStates(state, modelId) {
    return getTableSetting(state, modelId, 'groupStates')
}

export function getFilterOrder(state, modelId, defaultValue) {
    return get(getModel(state, modelId), `settings.filterOrder`, defaultValue);
}

export function getItemDataState(state, modelId) {
    return getModelPropOrFail(state, modelId, 'itemData');
}

export function getSaveResult(state, modelId) {
    return getModelPropOrFail(state, modelId, 'saveResult');
}

export function getDeleteResult(state, modelId) {
    return getModelPropOrFail(state, modelId, 'deleteResult');
}

export function getPrimaryKeyAttr(state, modelId) {
    return getDataModelProp(state, modelId, 'dataModel.primaryKey');
}

export function getItemCustomActionResult(state, modelId) {
    return getModelPropOrFail(state, modelId, 'itemCustomActionResult');
}

export function getCustomActionResult(state, modelId) {
    return getModelPropOrFail(state, modelId, 'customActionResult');
}

export function getConfirmNavigationMessages(state) {
    return state.ui.confirmNavigationMessages;
}

export function getRouter(state) {
    return state.router;
}

export function getLoadedItemId(state, modelId) {
    return getModelPropOrFail(state, modelId, 'itemData.value.itemId');
}

export function presentMessages(state, modelId, source) {
    const messages = get(state,`${source}.value.messages`) || getModelPropOrDefault(state, modelId, `${source}.value.messages`, []);

    return messages.map((message) => {
        return { ...message, source }
    })
}

export function getSettings(state) {
    return state.ui.settings;
}

export function getModelSettings(state, modelId) {
    return getModelPropOrDefault(state, modelId, "settings")
}

export function presentAllMessages(state, modelId, sources) {
    return sources.reduce((theMessages, source) => {
        theMessages = [...theMessages, ...presentMessages(state, modelId, source)];
        return theMessages;
    }, []);
}

export function presentError(state, modelId, source) {
    let error = getModelPropOrDefault(state, modelId, `${source}.error`);

    if(!error) { return null; }

    let message = stateTrans(state, 'unknownError');

    return { message, type: 'danger', source }
}

export function presentAllErrors(state, modelId, sources) {
    
    
    return sources.map((source) => {
        return presentError(state, modelId, source);
    }, []);
}

export function getEditFieldValidationMessage(state, modelId, fieldName) {
    let saveMessages = getModelPropOrFail(state, modelId, 'saveResult.value.messages');
    let validationMessages = get(find(saveMessages, { type:  'validation_error'}), `validation_messages.${fieldName}`);
    return isArray(validationMessages) ? validationMessages[0] : validationMessages;
}

export function getItemActions(state, modelId) {
    return getModelPropOrFail(state, modelId, 'itemData.value.actions');
}

export function getItemAction(state, modelId, name) {
    return find(getItemActions(state, modelId), {value: name});
}

export function getGlobalActions(state, modelId) {
    return getDataModelProp(state, modelId, 'dataModel.globalActions');
}

export function getGlobalAction(state, modelId, name) {
    return find(getGlobalActions(state, modelId), {value: name});
}

export function getGlobalActionsRequiresSelection(state, modelId) {
    return filter(getGlobalActions(state, modelId), "requires_selection");
}

export function getActionPermissions(state, modelId) {
    return getModelPropOrFail(state, modelId, 'itemData.value.actionPermissions');
}

export function getDataActionPermissions(state, modelId) {
    return getDataModelProp(state, modelId, 'dataModel.itemData.actionPermissions');
}

export function getCredentials(state) {
    return state.credentials;
}

export function getAuthResult(state) {
    return state.authResult;
}

export function getResetPasswordResult(state, part) {
    return get(state.resetPassword, part, null);
}

export function isAuthenticated(state) {
    return !!state.authResult.value;
}

export function getCurrentViewOrNull(state) {
    let modelId = getCurrentModelId(state);

    if(!modelId) { return null; }

    try {
        return getModelPropOrFail(state, modelId, 'view');
    } catch(e) { return null; }
}

export function getSubviews(state, modelId) {
    return getDataModelProp(state, modelId, 'dataModel.item.subviews')
}

export function getSubviewsByModelName(state, modelName) {
    return get(state, `models.${modelName}.value.dataModel.item.subviews`, emptyObject);
}

export function findSubview(subviews, subviewAbbreviation) {
    let modelSubview = find(subviews, {model: subviewAbbreviation});
    if(modelSubview) { return modelSubview; }

    return get(subviews, subviewAbbreviation);
}

export function getSubview(state, modelId, subviewAbbreviation) {
    try {
        let subviews = getSubviews(state, modelId);
        return findSubview(subviews, subviewAbbreviation);
    } catch(e) {
        return null;
    }
}

export function getSubviewByModelName(state, modelName, subviewOrModelName) {
    let subviews = getSubviewsByModelName(state, modelName);
    return findSubview(subviews, subviewOrModelName);
}

export function getSubviewName(state, modelId) {
    return getModelPropOrFail(state, modelId, 'subviewName')
}

export function getParentId(state, modelId) {
    return getModelPropOrFail(state, modelId, 'parentModelId');
}

export function getParentSubviews(state, modelId) {
    let parentModelId = getParentId(state, modelId);

    if(!parentModelId) { return { }; }

    return getSubviews(state, parentModelId);
}

export function getParentSubview(state, modelId) {
    let subviewName = getSubviewName(state, modelId),
        subviewData = {};

    if(subviewName) {
        let parentModelId = getParentId(state, modelId),
            parentSubview = getSubview(state, parentModelId, subviewName);

        subviewData = {...subviewData, parentModelId,parentSubview,subviewName}
    }

    return /** @type {parentModelId?: string,parentSubview?: import('./data/types/subview.types').PreparedSubview, subviewName?: string} */(subviewData);
}

export function getSubviewItemData(state, modelId) {
    let subviewData = getParentSubview(state,modelId);

    if(subviewData.subviewName) {
        let parentPrimaryKey = getPrimaryKeyAttr(state, modelId),
            parentItemId = getItemId(state, subviewData.parentModelId),
            parentItemState = getItemDataState(state, subviewData.parentModelId),
            parentItemValues = getStartValues(state, subviewData.parentModelId);

        subviewData = {...subviewData, parentPrimaryKey, parentItemId, parentItemState, parentItemValues};
    }

    return subviewData;
}

export function getAllResultStateInfos(state, modelId) {
    let resultState = getResultsState(state, modelId);

    return {
        ...getAllResultParams(state, modelId),
        modelName: getModelName(state, modelId),
        resultState,
        loadedResultParams: get(resultState, 'loadedParams'),
        loadingResultParams: get(resultState, 'loadingParams')
    }
}

export function getAllItemStateInfos(state, modelId) {
    let itemDataState = getItemDataState(state, modelId);

    return {
        ...getAllResultStateInfos(state, modelId),
        itemDataState: itemDataState,
        loadedItemId: get(itemDataState, 'value.itemId'),
        itemId: getItemId(state, modelId),
    }
}

export function getModelTitle(state, modelId) {
    return getDataModelProp(state, modelId, 'dataModel.title');
}


export function getNewRecordTitle(state, modelId) {
    return getDataModelProp(state, modelId, `stateModel.newRecordTitle`);
}

export function getRootModel(state, modelId) {
    let models = getLoadedModels(state);

    return modelTree(models).getRootModel(modelId);
}

export function getRootModelName(state, modelId) {
    return getRootModel(state, modelId).modelName;
}

export function getResultInfo(state, modelId) {
    return getDataModelProp(state, modelId, 'dataModel.resultParams.resultInfo');
}

export function getTitleColumn(state, modelId) {
    return getDataModelProp(state, modelId, 'dataModel.resultParams.resultInfo.titleColumn');
}

export function getSubviewModelId(state, modelId, subviewName) {
    let childModels = modelTree(getLoadedModels(state)).getChildren(modelId),
        subviewModel = find(childModels, { subviewName });

    return subviewModel.modelId;
}

export function getLastPath(state) {
    return get(state.router, 'previous.pathname');
}

export function getPath(state) {
    return get(state.router, 'pathname');
}

export function getLocationHistory(state) {
    return state.locationHistory;
}

export function getCloseModelId(state, modelId) {
    return getModelPropOrFail(state, modelId, 'closeModelId');
}

export function getAppPage(state) {
    return state.appPage;
}

export function getDialogPage(state) {
    return state.dialogPage;
}

export function getPageAction(state, modelId) {
    return getCurrentModelId(state) === modelId ? state.appPageAction : null;
}

export function getActiveMenuName(state) {
    let appPage = getAppPage(state);

    if(appPage !== identify.APP_PAGE_MODEL) {
        return getPath(state).substring(1);
    }

    let currentModelId = getCurrentModelId(state);
    if(!currentModelId) { return null; }

    return getRootModelName(state, getCurrentModelId(state));
}

export function getItemLink(state, modelId) {
    return getModelPropOrFail(state, modelId, 'itemData.value.link');
}

export function doResultsNeedPagination(state, modelId) {
    let results = getResultsValue(state, modelId),
        perPage = getResultPerPage(state, modelId);

    return results.total > perPage;
}

export function getTableSettingsIdentifier(state, modelId) {
    let subviewName = getSubviewName(state, modelId);

    if(subviewName && subviewName.length) { return subviewName; }

    return getModelName(state, modelId);
}

export function getPresentColumnsData(state, modelId) {
    let columns = getColumns(state, modelId),
        parentSubview = getParentSubview(state, modelId),
        tableSettingsIdentifier = getTableSettingsIdentifier(state, modelId),
        columnVisibility = getcolumnVisibility(state, modelId),
        columnOrder = getColumnOrder(state, modelId),
        view = getView(state, modelId);

    return {
        ...parentSubview,
        columns,
        tableSettingsIdentifier,
        columnVisibility,
        columnOrder,
        view,
    };
}

export function getActiveAppBarDialog(state) {
    return state.ui.activeAppBarDialog;
}

export function getAvailableModels(state) {
    return state.availableModels;
}

export function isAvailableModel(state, modelName) {
    return getAvailableModels(state).indexOf(modelName) > -1;
}

export function isModelCacheLoaded(state) {
    return !!state.modelCacheStatus.finished_at
}

export function isModelCacheLoading(state) {
    return state.modelCacheStatus.started_at && !state.modelCacheStatus.finished_at;
}

export function getSearchModels(state) {
    return state.ui.searchModels;
}

export function getSearchModel(state, modelName) {
    return state.ui.searchModels[modelName];
}

export function getSearchTerm(state) {
    return state.ui.searchTerm;
}

export function getSearchResultSort(state, modelName) {
    // let sortSettings = getRawTableSetting(state, modelName, 'sortOptions');

    // if(sortSettings) { return sortSettings; }

    return getSearchModel(state, modelName).resultParams.sortOptions;
}

export function getSearchFilter(state, modelName) {
    let dataModel = getDataModel(state, modelName);

    return getOrFail(dataModel, 'dataModel.searchFilter');
}

export function getArchiveFilter(state, modelName) {
    let dataModel = getDataModel(state, modelName);

    return getOrFail(dataModel, 'dataModel.archiveFilter');
}


export function getSearchFilterByModelId(state, modelId) {
    if (getModelPropOrFail(state, modelId, `subviewName`)) return "";
    return getSearchFilter(state, getModelName(state, modelId));
}

export function getAllSearchResultParams(state, modelName) {
    let searchTerm = getSearchTerm(state),
        searchModel = getSearchModel(state, modelName),
        searchFilter = getSearchFilter(state, modelName),
        searchSort = getSearchResultSort(state, modelName);

    return presentSearchResultParams({searchModel, searchTerm, searchFilter, searchSort});
}

export function getFirstSearchError(state) {
    let searchModels = state.ui.searchModels;
    
    let erroredModel = find(searchModels, (model) => {
        return !!model.results.error;
    });
    
    if(erroredModel) {
        return erroredModel.results.error;        
    }
    
    return null;
}

export function presentSearchErrors(state) {
    let errors = [];
    let firstError = getFirstSearchError(state);
    
    if(firstError) {
        errors = [{ message: stateTrans(state, 'unknownError'), type: 'danger' }];
    }
    
    return errors;
}

export function getCreatePath(state, modelId) {
    return getDataModelPropOrDefault(state, modelId, "dataModel.createPath");
}


export function getSearchFilterValue(state, modelId, minLength = 1) {
    try {
        const modelSearchFilter = getSearchFilterByModelId(state, modelId),
              searchFilterValue = !modelSearchFilter ? "" : getFieldValue(state, modelId, identify.FILTERFIELD, modelSearchFilter)?.value || "";
    
        return searchFilterValue.length < minLength ? "" : searchFilterValue;
    } catch {
        return "";
    }
}